import { Box, Group, Paper, Stack, Text } from "@mantine/core";
import { IconChevronLeftOutline18, IconChevronRightOutline18 } from "nucleo-ui-outline-18";
import { useTranslation } from "react-i18next";

import type { SupportedLanguage } from "@mountayaapp/locales";
import { ICON_SIZE } from "@mountayaapp/theme";

import { normalizeHref } from "../../content/rules/href";
import { getNavManifest, type NavItem } from "../../content/rules/registry";

/**
 * DocsPagerProps carry the active pathname (matched against the manifest to find
 * the owning section and the current page within it) and the active language
 * (selects the manifest the reading order is read from).
 */
interface DocsPagerProps {
  lang: SupportedLanguage;
  pathname: string;
}

/**
 * findSectionNeighbours locates the current page within its OWNING section's
 * items and returns the pages immediately before and after it inside that
 * section only, each `null` at a section boundary (no previous on the section's
 * first page, no next on its last) or when the pathname matches no page in any
 * section. Scoping to the owning section is deliberate: the pager walks the
 * reading order one page at a time but never steps across a section boundary, so
 * the last page of one section has no next rather than jumping into the next
 * section. Comparison runs through `normalizeHref` so a trailing-slash
 * difference between the live pathname and a manifest href still matches.
 */
export const findSectionNeighbours = (
  lang: SupportedLanguage,
  pathname: string,
): { next: NavItem | null; previous: NavItem | null } => {
  const current = normalizeHref(pathname);

  for (const group of getNavManifest(lang)) {
    const index = group.items.findIndex((item) => normalizeHref(item.href) === current);
    if (index !== -1) {
      return {
        next: group.items[index + 1] ?? null,
        previous: group.items[index - 1] ?? null,
      };
    }
  }

  return { next: null, previous: null };
};

/**
 * PagerCardProps carry the neighbour page the card links to, the localized
 * eyebrow above its title, and the direction the card reads in: a `previous`
 * card leads with a back chevron and left-aligns, a `next` card trails a forward
 * chevron and right-aligns.
 */
interface PagerCardProps {
  direction: "next" | "previous";
  eyebrow: string;
  item: NavItem;
}

/**
 * PagerCard is one previous/next card: a bordered surface wrapping a full-reload
 * anchor to the neighbour page, with the eyebrow stacked over the page title.
 * The previous variant reads left-to-right behind a back chevron; the next
 * variant right-aligns its text and trails a forward chevron, so the pair points
 * outward toward the direction each one travels.
 */
const PagerCard = ({ direction, eyebrow, item }: PagerCardProps) => {
  const isNext = direction === "next";

  return (
    <Paper component="a" href={item.href} p="md" withBorder>
      <Group
        align="center"
        c="var(--mantine-color-text)"
        gap="sm"
        justify={isNext ? "flex-end" : "flex-start"}
      >
        {isNext ? null : <IconChevronLeftOutline18 size={ICON_SIZE.sm} />}
        <Stack align={isNext ? "flex-end" : "flex-start"} gap="xxs">
          <Text c="dimmed" size="xs" tt="uppercase">
            {eyebrow}
          </Text>
          <Text fw={600}>{item.title}</Text>
        </Stack>
        {isNext ? <IconChevronRightOutline18 size={ICON_SIZE.sm} /> : null}
      </Group>
    </Paper>
  );
};

/**
 * DocsPager is the previous/next footer beneath a page's body: up to two cards
 * that walk the current section's reading order one page at a time, the previous
 * page on the left and the next on the right. Each card is a full-reload anchor
 * like the sidebar links, since the docs app serves static pages with no client
 * router. The pager stays within the owning section: it omits the previous card
 * on the section's first page and the next card on its last, keeping the
 * remaining card in its own column so a sole next card stays right-aligned. It
 * renders nothing when the pathname matches no page in any section.
 */
export const DocsPager = ({ lang, pathname }: DocsPagerProps) => {
  const { t } = useTranslation("docs");
  const { next, previous } = findSectionNeighbours(lang, pathname);

  if (!previous && !next) {
    return null;
  }

  return (
    <Group align="stretch" gap="md" grow mt="xl">
      {previous ? (
        <PagerCard direction="previous" eyebrow={t("pager.previous")} item={previous} />
      ) : (
        <Box />
      )}
      {next ? <PagerCard direction="next" eyebrow={t("pager.next")} item={next} /> : <Box />}
    </Group>
  );
};
